import * as Sentry from '@sentry/react'

const IGNORED_TRACE_REQUESTS = [
    '/cashier-app/money-transfer/voucher/draft',
    '/cashier-app/money-transfer/voucher/draft/changes'
]
const DEFAULT_TRACE_PROPAGATION_TARGETS = [
    /^https:\/\/staging-vcash-sbu\.fly\.dev(\/|$)/,
    /^https:\/\/api\.vcash\.rs(\/|$)/,
    /^https:\/\/staging\.api\.vcash\.rs(\/|$)/
]

const toRate = (value: string | undefined, fallback: number): number => {
    const parsed = Number(value)
    if (Number.isNaN(parsed)) {
        return fallback
    }
    return Math.min(Math.max(parsed, 0), 1)
}

const getTracePropagationTargets = (): Array<string | RegExp> => {
    const targets: Array<string | RegExp> = [...DEFAULT_TRACE_PROPAGATION_TARGETS]
    const apiUrl = import.meta.env.VITE_API_URL?.trim()

    if (!apiUrl) {
        return targets
    }

    try {
        targets.push(new URL(apiUrl).origin)
    } catch {
        targets.push(apiUrl)
    }

    return targets
}

export const initSentry = () => {
    const dsn = import.meta.env.VITE_SENTRY_DSN?.trim()
    const isEnabled = import.meta.env.VITE_SENTRY_ENABLED === 'true'
    const sentryRelease =
        import.meta.env.VITE_SENTRY_RELEASE ||
        (globalThis as { __SENTRY_RELEASE__?: string }).__SENTRY_RELEASE__

    if (!isEnabled || !dsn) {
        return
    }

    Sentry.init({
        dsn,
        enableLogs: true,
        environment:
            import.meta.env.VITE_SENTRY_ENVIRONMENT || import.meta.env.MODE,
        release: sentryRelease,
        integrations: [
            Sentry.browserTracingIntegration({
                // Prevent high-frequency polling from extending navigation
                // transactions for the entire time the user stays on screen.
                shouldCreateSpanForRequest: (url) =>
                    !IGNORED_TRACE_REQUESTS.some((path) => url.includes(path))
            }),
            Sentry.replayIntegration({
                maskAllText: false,
                blockAllMedia: false,
                maskAllInputs: false,
                networkDetailAllowUrls: [/.*/],
                // Registry bearer credentials and template packages must not be
                // captured by the broad network-detail rule above.
                networkDetailDenyUrls: [
                    import.meta.env.VITE_RECEIPT_REGISTRY_URL?.trim()
                ].filter((url): url is string => Boolean(url)),
                networkRequestHeaders: ['Authorization','X-Session-ID'],
                networkResponseHeaders: ['Authorization','X-Session-ID'],
            })
        ],
        tracesSampleRate: toRate(
            import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE,
            0.1
        ),
        tracePropagationTargets: getTracePropagationTargets(),
        // Kiosk replay starts manually after the idle advert overlay is dismissed.
        replaysSessionSampleRate: 0,
        replaysOnErrorSampleRate: 0
    })
}

export const startReplayRecording = () => {
    Sentry.getReplay()?.start()
}

export const stopReplayRecording = async () => {
    await Sentry.getReplay()?.stop()
}

export const setKioskUser = ({
    deviceName,
    email
}: {
    deviceName: string
    email?: string
}) => {
    Sentry.setUser({
        id: deviceName,
        username: deviceName,
        email
    })
    Sentry.setTag('kiosk_id', deviceName)
    if (email) {
        Sentry.setTag('kiosk_email', email)
    }
}
